import React, { useState } from "react";
import { Box, Input, Separator, Text, Button, Checkbox } from "@showitinc/showit-wc";
import ButtonBar from "../SetupButtonBar";
import ViewPanel from "../ViewPanel";
import useValidateWordpressLogin from "../hooks/useValidateWordpressLogin";
import styles from "../BlogSetupWizard.module.css";

function canVerify(setupData) {
  const { blog_admin_url, blog_admin_user, blog_admin_pass } = setupData;

  return blog_admin_url && blog_admin_user && blog_admin_pass;
}

function CurrentBlogLogin({ blogSetup: { setupData }, actions }) {
  const [showPassword, setShowPassword] = useState(false);
  const { blog_admin_verified } = setupData;
  const [validateLogin, { isPending, isError }] = useValidateWordpressLogin();

  const proceed = () => {
    actions.goToNextStep();
  };

  const verify = async () => {
    const { blog_admin_url, blog_admin_user, blog_admin_pass } = setupData;
    await validateLogin(
      { url: blog_admin_url, username: blog_admin_user, password: blog_admin_pass },
      {
        onSuccess: (data) => {
          const { login } = data;
          if (login.isValid) {
            actions.goToNextStep({ blog_admin_verified: login.isValid ? "login" : undefined });
          } else {
            actions.updateSetup({ blog_admin_verified: "invalid" });
          }
        },
      }
    );
  };

  let btnAction;
  let btnText = "Verify";
  const showWarning = blog_admin_verified === "invalid" || blog_admin_verified === "user";

  if (blog_admin_verified === "login" || blog_admin_verified === "user") {
    btnAction = proceed;
    btnText = "Next";
  } else {
    if (canVerify(setupData)) {
      btnAction = verify;
    }
  }

  if (isPending) {
    btnAction = undefined;
    btnText = "Verifying";
  }

  return (
    <ViewPanel>
      {{
        content: (
          <>
            <Text variant="heading-2" variantStyle="strong">
              Current Blog Login
            </Text>
            <Separator />
            <Text>Now we need the login information for current blog.</Text>
            <Box my="3">
              <Box mb="3">
                <Text>
                  What url do you use to log in to your blog?
                  <br />
                  <span className="muted">
                    Please include http:// or https:// at the beginning.
                  </span>
                </Text>
                <Input
                  type="text"
                  name="blog_admin_url"
                  placeholder="https://example.com/wp-login.php"
                  value={setupData.blog_admin_url}
                  longLabel
                  onSIChange={(e) =>
                    actions.updateSetup({
                      blog_admin_url: e.target.value,
                      blog_admin_verified: undefined,
                    })
                  }
                />
              </Box>
              <Box mb="3">
                <Text>
                  To access and migrate your content, please provide login information for your
                  existing blog.
                </Text>
              </Box>
              <Input
                type="text"
                name="blog_admin_user"
                label="Username or Email"
                value={setupData.blog_admin_user}
                longLabel
                onSIInput={(e) =>
                  actions.updateSetup({
                    blog_admin_user: e.target.value,
                    blog_admin_verified: undefined,
                  })
                }
              />
              <Input
                type={showPassword ? "text" : "password"}
                name="blog_admin_pass"
                label="Password"
                value={setupData.blog_admin_pass}
                longLabel
                onSIInput={(e) =>
                  actions.updateSetup({
                    blog_admin_pass: e.target.value,
                    blog_admin_verified: undefined,
                  })
                }
              >
                <Button
                  slot="suffix"
                  variant="link"
                  style={{ paddingTop: 14, fontSize: 10 }}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? "Hide" : "Show"}
                </Button>
              </Input>
              {blog_admin_verified === "login" ? (
                <div className="has-success">
                  <p className="help-block" style={{ textAlign: "center" }}>
                    Login Verified
                  </p>
                </div>
              ) : undefined}

              {showWarning ? (
                <>
                  <Text className={styles["error-text"]}>
                    We could not log in to your blog with the info above.
                  </Text>
                  <Checkbox
                    type="checkbox"
                    name="current_blog_legit"
                    label="I can confirm that the credentials are correct."
                    checked={blog_admin_verified === "user"}
                    onSIChange={(e) =>
                      actions.updateSetup({
                        blog_admin_verified: e.target.checked ? "user" : undefined,
                      })
                    }
                  />
                </>
              ) : undefined}
            </Box>
          </>
        ),
        actions: (
          <ButtonBar
            onForward={btnAction}
            onBack={actions.goToPrevStep}
            allowForward={btnAction !== undefined}
            allowBack
            forwardBtnText={btnText}
          />
        ),
      }}
    </ViewPanel>
  );
}

export default CurrentBlogLogin;
